// @spec IAM-STR-031
'use client';

import { AlertTriangle, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useEndImpersonation, useIsImpersonating } from '../api/use-impersonation';
import { useImpersonationStore } from '../stores/impersonation-store';

import { Button } from '@/components/ui/button';

/**
 * Banner displayed when a Super Admin is impersonating another user.
 *
 * Shows the impersonated user's info and provides an "End Session" button.
 * Fixed at the top of the viewport.
 *
 * @example
 * // Typically used in ImpersonationProvider, not directly
 * <ImpersonationBanner />
 */
export function ImpersonationBanner() {
  const { t } = useTranslation('admin');
  const isImpersonating = useIsImpersonating();
  const targetUser = useImpersonationStore.use.targetUser();
  const { mutate: endImpersonation, isPending } = useEndImpersonation();

  if (!isImpersonating || !targetUser) {
    return null;
  }

  const computedName = [targetUser.firstName, targetUser.lastName].filter(Boolean).join(' ');
  const displayName = targetUser.displayName ?? (computedName || targetUser.email);

  const handleEndSession = () => {
    endImpersonation();
  };

  return (
    <div
      className="fixed inset-x-0 top-0 z-50 flex items-center justify-between gap-4 bg-amber-500 px-4 py-2 text-amber-950 shadow-md"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="h-5 w-5 flex-shrink-0" />
        <span className="font-medium hidden sm:inline">{t('impersonation.impersonating')}</span>
        <span className="truncate font-medium sm:font-normal">{displayName}</span>
        <span className="hidden md:inline text-amber-800">({targetUser.email})</span>
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={handleEndSession}
        disabled={isPending}
        className="flex-shrink-0 border-amber-700 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:text-amber-950"
      >
        {isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="hidden sm:inline">{t('impersonation.ending')}</span>
          </>
        ) : (
          <>
            <X className="h-4 w-4 sm:hidden" />
            <span className="hidden sm:inline">{t('impersonation.endSession')}</span>
          </>
        )}
      </Button>
    </div>
  );
}
